import { type ForgeDoc } from '@atlassian/forge-ui-types';
import React from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { type Extension } from '../web-client';
import { trackComponentUsageRendered } from '../analytics/useForgeUiAnalyticsEvent';
import { useMetricsContext } from '../context/metrics';

type ComponentName = string;
type ComponentInformation = {
	count: number;
	deprecatedPropsList?: string[];
	propCount: Counts['propCount'];
	uniqueGlyphs?: string[];
	widthValues?: string[];
};
type PropName = string;
type Counts = {
	componentCount: number;
	deprecatedPropsList?: Set<string>;
	propCount: Record<PropName, number>;
	uniqueGlyphs?: Set<string>;
	widthValues?: Set<string>;
};
type CountAnalytics = Record<ComponentName, Counts>;
type ForgeDocVisitor = (forgeDoc: ForgeDoc, componentCountMap: CountAnalytics) => void;

// Gets the count of props on a component (now a visitor)
export const getPropCount: ForgeDocVisitor = (doc, componentCountMap) => {
	if (!doc.props) {
		return;
	}

	Object.keys(doc.props).forEach((propName) => {
		if (propName === 'children') {
			return;
		}

		const componentName = doc.type;
		if (componentCountMap[componentName].propCount[propName] !== undefined) {
			componentCountMap[componentName].propCount[propName] += 1;
		} else {
			componentCountMap[componentName].propCount[propName] = 1;
		}
	});
};

const getIconInformation: ForgeDocVisitor = (doc, componentCountMap) => {
	if (doc.type !== 'Icon' || !doc.props) {
		return;
	}
	const { glyph, primaryColor, secondaryColor } = doc.props;

	if (!componentCountMap.Icon.uniqueGlyphs) {
		componentCountMap.Icon.uniqueGlyphs = new Set();
	}
	if (glyph) {
		componentCountMap.Icon.uniqueGlyphs.add(glyph);
	}

	// TODO: Stop tracking these props once they have been removed from the code base
	// https://ecosystem-platform.atlassian.net/browse/EXT-3350
	if (!componentCountMap.Icon.deprecatedPropsList) {
		componentCountMap.Icon.deprecatedPropsList = new Set();
	}
	if (primaryColor) {
		componentCountMap.Icon.deprecatedPropsList.add('primaryColor');
	}
	if (secondaryColor) {
		componentCountMap.Icon.deprecatedPropsList.add('secondaryColor');
	}
};

const getModalInformation: ForgeDocVisitor = (doc, componentCountMap) => {
	if (doc.type !== 'Modal' || !doc.props) {
		return;
	}
	const { width } = doc.props;

	if (width === 'fullscreen') {
		if (!componentCountMap.Modal.widthValues) {
			componentCountMap.Modal.widthValues = new Set();
		}
		componentCountMap.Modal.widthValues.add('fullscreen');
	}
};

// Recursive function to get the total number of components and total number of props on a component
const getCountAnalytics = (
	forgeDoc: ForgeDoc,
	componentCountMap: CountAnalytics = {},
	visitors: ForgeDocVisitor[] = [],
) => {
	const componentName = forgeDoc.type;
	// Type String is not a component
	if (componentName === 'String') {
		return componentCountMap;
	}
	// Don't send Root count as it's always one
	if (componentName !== 'Root') {
		const hasComponentEncountered = componentCountMap[componentName] !== undefined;
		if (hasComponentEncountered) {
			componentCountMap[componentName].componentCount += 1;
		} else {
			componentCountMap[componentName] = { componentCount: 1, propCount: {} };
		}
		// Call all visitor functions
		visitors.forEach((visitor) => {
			visitor(forgeDoc, componentCountMap);
		});
	}

	forgeDoc.children.forEach((forgeDocChild) => {
		getCountAnalytics(forgeDocChild, componentCountMap, visitors);
	});

	return componentCountMap;
};

const createKey = (countMap: CountAnalytics) => {
	let key = '';
	Object.keys(countMap)
		.sort()
		.forEach((componentName) => {
			key += `${componentName}${countMap[componentName].componentCount}`;
		});
	return key;
};

interface Args {
	forgeDoc: ForgeDoc | undefined;
	forgeEnvironment: Extension['environmentType'];
	UIKitVersion: '1' | '2';
}

// Gets the total number of components and props to send for analytics
// Only sends when there is a new count of components that has not been sent before
export const useSendNodeCountAnalytics = ({
	forgeDoc,
	forgeEnvironment,
	UIKitVersion,
}: Args): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { page: source } = useMetricsContext();

	const prevCountMap = React.useRef<Record<string, true>>({});

	if (forgeDoc === undefined) {
		return;
	}

	const componentCount = getCountAnalytics(forgeDoc, {}, [
		getPropCount,
		getIconInformation,
		getModalInformation,
	]);

	const componentCountAsKey = createKey(componentCount);

	// converting to a string here as a safety measure against mutations to the original forgeDoc object
	const forgeReactMajorVersion = forgeDoc.forgeReactMajorVersion?.toString() ?? null;

	// compare if a previous count has already been sent for analytics
	if (!prevCountMap.current.hasOwnProperty(componentCountAsKey)) {
		prevCountMap.current = {
			...prevCountMap.current,
			[componentCountAsKey]: true,
		};

		// NOTE: createAnalyticsEvent has a side effect of potentially mutating objects that are passed into it
		// Any objects passed in should not be reused or must be deep cloned first
		const components: Record<ComponentName, ComponentInformation> = {};
		for (let componentName in componentCount) {
			if (componentName === 'Icon') {
				components[componentName] = {
					count: componentCount[componentName].componentCount,
					propCount: componentCount[componentName].propCount,
					uniqueGlyphs: Array.from(componentCount[componentName].uniqueGlyphs ?? []),
					deprecatedPropsList: Array.from(componentCount[componentName].deprecatedPropsList ?? []),
				};
			} else if (componentName === 'Modal') {
				components[componentName] = {
					count: componentCount[componentName].componentCount,
					propCount: componentCount[componentName].propCount,
					widthValues: Array.from(componentCount[componentName].widthValues ?? []),
				};
			} else {
				components[componentName] = {
					count: componentCount[componentName].componentCount,
					propCount: componentCount[componentName].propCount,
				};
			}
		}

		trackComponentUsageRendered(createAnalyticsEvent, {
			source,
			isLegacyUiKit: UIKitVersion === '1',
			forgeEnvironment,
			components,
			forgeReactMajorVersion,
		});
	}
};
